<template>
  <div class="paycripto-box">
    <!-- Lanzador con imagen -->
    <v-btn 
      :block="block"
      depressed
      color="transparent"
      class="pc-launcher"
      :disabled="loading"
      @click="open"
    >
      <v-img
        :src="buttonImageUrl"
        contain
        :max-height="buttonMaxHeight"
        :max-width="buttonMaxWidth"
        class="mx-auto"
      />
    </v-btn>

    <!-- Popup -->
    <v-dialog
      v-model="dialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :max-width="maxWidth"
      scrollable
      :persistent="persistent"
      :overlay-color="'#0b1220'"
      :overlay-opacity="0.75"
      content-class="pc-dialog"
      @input="v => { if (!v) $emit('closed') }"
    >
      <v-card class="pc-card">
        <!-- Header -->
        <div class="pc-header">
          <div class="pc-chip">
            <v-icon size="18" class="mr-1">mdi-bitcoin</v-icon>
            Recarga con cripto
          </div>
          <h3 class="pc-title">{{ title }}</h3>
          <v-btn icon class="pc-close" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Cuerpo -->
        <v-card-text class="pc-body">
          <!-- FORM MINIMO: monto + cripto -->
          <v-form v-if="step===1" ref="form" v-model="valid" lazy-validation class="pc-form">
            <div class="pc-amount">
              <label class="pc-amount__label">Monto a depositar</label>
              <v-text-field
                v-model.number="amount"
                type="number"
                min="0"
                step="0.01"
                prefix="$"
                :rules="[v => v!=='' || 'Requerido', v => Number(v) > 0 || 'Debe ser mayor a 0']"
                hide-details="auto"
                outlined dense class="pc-amount__input"
              />
              <small class="pc-amount__hint">Se cotiza a la tasa del proveedor al momento del pago.</small>
            </div>

            <div class="pc-grid">
              <v-select
                v-model="crypto"
                :items="cryptos"
                label="Criptomoneda"
                :rules="[v => !!v || 'Requerido']"
                outlined dense class="pc-input"
              />
              <v-select
                v-if="showNetwork"
                v-model="network"
                :items="networks"
                label="Red"
                outlined dense class="pc-input"
              />
            </div>

            <v-btn class="pc-cta" :loading="loading" :disabled="!valid || loading" @click="startCheckout">
              <v-icon left>mdi-lock</v-icon> Generar enlace de pago
            </v-btn>
          </v-form>

          <!-- Loading -->
          <div v-else-if="loading && step===2 && !payUrl && !payHtml" class="pc-loading">
            <div class="pc-spinner"></div>
            Creando orden cripto…
          </div>

          <!-- Error -->
          <v-alert v-if="errorMsg" type="error" dense outlined class="mb-3">
            {{ errorMsg }}
          </v-alert>

          <!-- Modo URL: iFrame con fallback -->
          <div v-if="step===2 && payUrl" class="pc-iframe-wrap">
            <iframe
              v-show="iframeOk"
              :src="payUrl"
              class="pc-iframe"
              frameborder="0"
              allow="payment *; clipboard-write *"
              @load="onIframeLoad"
            />
            <div v-if="!iframeOk" class="pc-loading pc-loading--center" style="padding:16px;">
              <div class="pc-spinner"></div>
              <span v-if="!embedFailed" class="ml-2">Cargando checkout cripto…</span>
              <span v-else class="ml-2">
                El proveedor no permite iFrame.
                <a :href="payUrl" target="_blank" rel="noopener">Abrir en nueva pestaña</a>.
              </span>
            </div>
            <div class="pc-actions-inline">
              <v-btn text small class="mr-2" :href="payUrl" target="_blank" rel="noopener">
                <v-icon left small>mdi-open-in-new</v-icon> Abrir en nueva pestaña
              </v-btn>
              <v-btn text small @click="copyUrlOrHtml">
                <v-icon left small>mdi-content-copy</v-icon> Copiar URL
              </v-btn>
            </div>
          </div>

          <!-- Modo HTML: renderizar body() del backend -->
          <div v-if="step===2 && payHtml" class="pc-iframe-wrap">
            <iframe
              class="pc-iframe"
              frameborder="0"
              allow="payment *; clipboard-write *"
              :srcdoc="payHtml"
              @load="onIframeLoad"
            />
            <div class="pc-actions-inline">
              <v-btn
                v-if="htmlBlobUrl"
                text small class="mr-2"
                :href="htmlBlobUrl"
                target="_blank" rel="noopener"
              >
                <v-icon left small>mdi-open-in-new</v-icon> Abrir en nueva pestaña
              </v-btn>
              <v-btn text small @click="copyUrlOrHtml">
                <v-icon left small>mdi-content-copy</v-icon> Copiar HTML
              </v-btn>
            </div>
          </div>
        </v-card-text>

        <!-- Footer -->
        <v-card-actions class="pc-actions">
          <v-spacer />
          <v-btn v-if="!loading && !payUrl && !payHtml" small outlined class="mr-2" @click="startCheckout">
            <v-icon left small>mdi-refresh</v-icon> Reintentar
          </v-btn>
          <v-btn text class="pc-secondary" @click="close">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Paycripto',
  props: {
    // Backend
    basePath: { type: String, default: 'https://apitr.xyz/onlineapi/api' },
    endpointStart: { type: String, default: '/paycrypto/start' },

    // Defaults / Catálogos
    defaultAmount: { type: [Number, String], default: '' },
    defaultCrypto: { type: String, default: 'USDT' },
    defaultNetwork: { type: String, default: 'Polygon' },
    cryptos: { type: Array, default: () => ['USDT','USDC'] },
    networks: { type: Array, default: () => ['Polygon','TRC20','ERC20','BEP20'] },
    showNetwork: { type: Boolean, default: true },

    // Dónde buscar la URL de pago en JSON
    responseUrlFields: { type: Array, default: () => ['iframeUrl','checkoutUrl','payUrl','redirectUrl','url','data.url'] },

    // UI
    title: { type: String, default: 'Recargar con cripto' },
    block: { type: Boolean, default: true },
    maxWidth: { type: [Number, String], default: 680 },
    persistent: { type: Boolean, default: false },
    buttonImageUrl: { type: String, default: 'https://dummyimage.com/220x90/0f172a/ffffff&text=Recargar+cripto' },
    buttonMaxWidth: { type: [Number, String], default: 220 },
    buttonMaxHeight: { type: [Number, String], default: 90 },

    // Comportamiento
    iframeTimeoutMs: { type: Number, default: 4500 },
    postMessageDoneType: { type: String, default: 'CRYPTO_TOPUP_DONE' },
    postMessageErrorType: { type: String, default: 'CRYPTO_TOPUP_ERROR' },
  },
  data() {
    return {
      dialog: false,
      valid: false,
      step: 1,            // 1=form, 2=checkout
      loading: false,
      amount: '',
      crypto: '',
      network: '',

      // Modo URL
      payUrl: '',
      // Modo HTML
      payHtml: '',
      htmlBlobUrl: '',

      iframeOk: false,
      embedFailed: false,
      iframeTimeoutId: null,

      orderId: null,
      clientTxId: null,
      errorMsg: '',
    };
  },
  mounted() {
    this.amount = this.defaultAmount;
    this.crypto = this.defaultCrypto;
    this.network = this.defaultNetwork;
    window.addEventListener('message', this.onMessage);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onMessage);
    this.revokeBlob();
  },
  methods: {
    open() {
      this.dialog = true;
      this.step = 1;
    },
    close() {
      this.dialog = false;
      this.reset();
    },
    reset() {
      this.loading = false;
      this.payUrl = '';
      this.payHtml = '';
      this.revokeBlob();
      this.iframeOk = false;
      this.embedFailed = false;
      this.errorMsg = '';
      if (this.iframeTimeoutId) {
        clearTimeout(this.iframeTimeoutId);
        this.iframeTimeoutId = null;
      }
    },
    revokeBlob() {
      if (this.htmlBlobUrl) {
        URL.revokeObjectURL(this.htmlBlobUrl);
        this.htmlBlobUrl = '';
      }
    },
    buildClientTxId() {
      const ts = Date.now();
      const rnd = Math.random().toString(36).slice(2, 8);
      return `PPC-${ts}-${rnd}`; // < 50 chars
    },
    readPath(obj, path) {
      try { return path.split('.').reduce((acc, k) => acc?.[k], obj) || null; }
      catch (err) { console.error('readPath error', err); return null; }
    },
    makeHtmlBlob() {
      try {
        this.revokeBlob();
        const blob = new Blob([this.payHtml], { type: 'text/html' });
        this.htmlBlobUrl = URL.createObjectURL(blob);
      } catch (err) {
        // Fallback: si falla la creación del blob o no está soportado
        this.htmlBlobUrl = '';
        console.error('makeHtmlBlob failed', err);
      }
    },
    async startCheckout() {
      const ok = await this.$refs.form.validate();
      if (!ok || this.loading) return;

      this.loading = true;
      this.errorMsg = '';
      this.iframeOk = false;
      this.embedFailed = false;
      this.payUrl = '';
      this.payHtml = '';
      this.revokeBlob();
      this.step = 2;

      try {
        this.clientTxId = this.buildClientTxId();
        const payload = {
          clientTxId: this.clientTxId,
          amount: Number(this.amount),
          crypto: this.crypto,
          network: this.showNetwork ? this.network : undefined,
        };

        // Acepta HTML o JSON
        const resp = await axios.post(
          `${this.basePath}${this.endpointStart}`,
          payload,
          {
            headers: { 'Accept': 'text/html,application/json' },
            responseType: 'text',
            transformResponse: [v => v],
          }
        );

        const rawText = typeof resp.data === 'string' ? resp.data : String(resp.data);

        // Intentar JSON primero
        let json = null;
        try { json = JSON.parse(rawText); } catch (err) { json = null; }

        if (json && typeof json === 'object') {
          this.orderId = json?.orderId || json?.ordenId || json?.id || null;

          let url = null;
          for (const p of this.responseUrlFields) {
            url = this.readPath(json, p);
            if (url) break;
          }
          if (!url) {
            const raw = json?.raw || {};
            url = raw.url || raw.payment_url || null;
          }

          if (!url) throw new Error('El backend no devolvió URL ni HTML.');
          this.payUrl = url;
        } else {
          // No era JSON -> asumimos HTML del gateway
          this.orderId = null;
          this.payHtml = rawText;
          this.makeHtmlBlob();
        }

        this.$emit('ready', {
          orderId: this.orderId,
          clientTxId: this.clientTxId,
          url: this.payUrl || null,
          raw: json ?? rawText,
          mode: this.payUrl ? 'url' : 'html',
        });

        this.iframeTimeoutId = setTimeout(() => {
          if (!this.iframeOk) this.embedFailed = true;
        }, this.iframeTimeoutMs);
      } catch (e) {
        console.error(e);
        this.errorMsg = e?.response?.data?.message || e.message || 'Error iniciando pago cripto';
        this.$emit('error', this.errorMsg);
        this.step = 1;
      } finally {
        this.loading = false;
      }
    },
    onIframeLoad() {
      this.iframeOk = true;
      this.embedFailed = false;
      if (this.iframeTimeoutId) {
        clearTimeout(this.iframeTimeoutId);
        this.iframeTimeoutId = null;
      }
    },
    onMessage(e) {
      const msg = e.data || {};
      if (msg.type === this.postMessageDoneType) {
        this.$emit('success', msg.payload || {});
        this.close();
      } else if (msg.type === this.postMessageErrorType) {
        this.$emit('error', msg.payload || 'Error en confirmación');
        this.close();
      }
    },
    async copyUrlOrHtml() {
      try {
        if (this.payUrl) {
          await navigator.clipboard.writeText(this.payUrl);
          this.$emit('copied', 'url');
        } else if (this.payHtml) {
          await navigator.clipboard.writeText(this.payHtml);
          this.$emit('copied', 'html');
        } else {
          this.$emit('error', 'Nada para copiar');
        }
      } catch (err) {
        this.$emit('error', 'No se pudo copiar al portapapeles');
        console.error('copyUrlOrHtml failed', err);
      }
    },
  },
};
</script>

<style scoped>
/* ===== Paleta clara minimalista ===== */
:root {
  --pc-bg: #ffffff;
  --pc-surface: #ffffff;
  --pc-border: #dce1ec;
  --pc-text: #0f172a;
  --pc-muted: #6b7280;
  --pc-primary: #0ea5e9;
  --pc-ring: rgba(14, 165, 233, .25);
}

/* Card */
.pc-card {
  background: var(--pc-bg);
  color: var(--pc-text);
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(2, 6, 23, .18);
  overflow: hidden;
}

/* Header */
.pc-header {
  position: relative;
  padding: 18px 20px 6px;
  background: linear-gradient(180deg,#ffffff,#f7f9fd);
  border-bottom: 1px solid #eef2f7;
}
.pc-title {
  margin: 10px 0 4px;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: .2px;
  color: #0f172a;
}
.pc-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  background: #ecfeff;
  border: 1px solid #cffafe;
  padding: 6px 10px;
  border-radius: 999px;
}
.pc-close { position: absolute; right: 6px; top: 6px; color: #6b7280; }

/* Body */
.pc-body { padding: 18px 18px 10px; }

/* Form */
.pc-form {
  background: var(--pc-surface);
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px;
}
.pc-grid { display:grid; grid-template-columns:1fr; grid-gap:12px; margin-top:6px; }
@media (min-width:520px){ .pc-grid{ grid-template-columns:1fr 1fr; } }
.pc-input :deep(.v-input__slot){
  border-radius:12px !important;
  min-height:48px !important;
  background:#ffffff !important;
  border:1px solid var(--pc-border) !important;
}

/* Monto */
.pc-amount {
  border:1.5px solid var(--pc-primary);
  border-radius:14px;
  padding:12px;
  margin-bottom:14px;
  box-shadow:0 0 0 4px var(--pc-ring);
  background:#ffffff;
}
.pc-amount__label {
  display:block;
  margin-bottom:8px;
  font-weight:700;
  color:#0f172a;
  font-size:13.5px;
}
.pc-amount__input :deep(.v-input__slot){
  border-radius:12px !important;
  min-height:54px !important;
  background:#ffffff;
  border:1px solid var(--pc-border) !important;
}
.pc-amount__input :deep(input){
  font-size:22px !important;
  font-weight:800 !important;
  letter-spacing:.5px;
  color:#0f172a !important;
}
.pc-amount__hint { display:block; margin-top:8px; font-size:12px; color:var(--pc-muted); }

/* CTA */
.pc-cta {
  margin-top:14px;
  width:100%;
  color:#fff !important;
  background:#0ea5e9 !important;
  text-transform:none;
  font-weight:800;
  letter-spacing:.2px;
  border-radius:12px;
  height:46px;
  box-shadow:0 10px 18px rgba(14,165,233,.18);
}

/* iFrame */
.pc-iframe-wrap { margin-top: 8px; border: 1px solid #eef2f7; border-radius: 14px; overflow: hidden; }
.pc-iframe { display:block; width:100%; height:680px; background:#fff; }

/* Footer */
.pc-actions { padding:10px 14px 16px; border-top:1px solid #eef2f7; }
.pc-secondary { color:#6b7280 !important; text-transform:none; }
.pc-actions-inline { display:flex; align-items:center; justify-content:flex-end; gap:8px; margin-top:10px; }

/* Loading */
.pc-loading { display:flex; align-items:center; justify-content:center; gap:10px; color:#6b7280; font-size:13px; margin:10px 0; }
.pc-spinner { width:18px; height:18px; border-radius:999px; border:2px solid #e5e7eb; border-top-color: var(--pc-primary); animation: pc-spin 1s linear infinite; }
@keyframes pc-spin { to { transform: rotate(360deg); } }

/* Launcher */
.pc-launcher { padding:0; min-height:auto; box-shadow:none; }
.pc-launcher:hover { transform:scale(1.02); }

/* Forzar fondo blanco del popup en móviles */
.pc-dialog .v-card, .pc-dialog .v-card > * { background-color:#ffffff !important; }
.v-overlay__scrim { background-color: rgba(11,18,32,.85) !important; }
@supports (-webkit-backdrop-filter:none) or (backdrop-filter:none){ .v-overlay__scrim{ -webkit-backdrop-filter:none !important; backdrop-filter:none !important; } }
.theme--dark .pc-dialog .v-card, .theme--dark .pc-dialog .v-card > * { background-color:#ffffff !important; color:#0f172a !important; }

/* Ajustes móviles */
@media (max-width:360px){ .pc-body{ padding:14px; } .pc-title{ font-size:18px; } }
</style>
